// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import StrokeOrderDiagram from "../StrokeOrderDiagram/StrokeOrderDiagramComponent";
import { buildMeaningOptions, buildReadingOptions, getMeaning } from "./wanikaniQuizHelpers";
import * as analytics from "../../utils/analytics";
import { useLocalizedPath } from "../../i18n/localizedPath";
import styles from "./wanikaniPage.module.scss";

function QuestionPrompt({ type }) {
    const { t } = useTranslation("wanikaniPage");
    if (type === "meaning") return t("quiz.promptMeaning");
    if (type === "reading") return t("quiz.promptReading");
    return t("quiz.promptStroke");
}

function WanikaniQuizComponent({ queue, onFinish, onExit }) {
    const { t, i18n } = useTranslation("wanikaniPage");
    const language = i18n.language;
    const [index, setIndex] = useState(0);
    const [phase, setPhase] = useState("question"); // question | feedback
    const [selected, setSelected] = useState(null);
    const [wasCorrect, setWasCorrect] = useState(null);
    const [score, setScore] = useState({ correct: 0, wrong: 0 });
    const [missed, setMissed] = useState([]);

    const item = queue[index];
    const meaningOptions = useMemo(
        () => (item?.type === "meaning" ? buildMeaningOptions(item.symbol, language) : null),
        [item, language]
    );
    const readingData = useMemo(
        () => (item?.type === "reading" ? buildReadingOptions(item.symbol) : null),
        [item]
    );

    function recordAnswer(correct) {
        setWasCorrect(correct);
        setPhase("feedback");
        setScore((current) => ({
            correct: current.correct + (correct ? 1 : 0),
            wrong: current.wrong + (correct ? 0 : 1),
        }));
        if (!correct) setMissed((current) => [...current, item.symbol]);

        analytics.track("quiz_question_answered", {
            quiz_type: "wanikani",
            question_type: item.type,
            symbol: item.symbol,
            correct,
            question_index: index,
        });
    }

    function handleOptionClick(option, correctOption) {
        /* istanbul ignore if -- unreachable: every option button is disabled once the question has been answered (phase is "feedback") */
        if (phase !== "question") return;
        setSelected(option);
        recordAnswer(option === correctOption);
    }

    function handleNext() {
        if (index + 1 >= queue.length) {
            onFinish({ score, missed });
            return;
        }
        setIndex((current) => current + 1);
        setPhase("question");
        setSelected(null);
        setWasCorrect(null);
    }

    if (!item) return null;

    return (
        <div className={styles.quizCard}>
            <div className={styles.quizProgress}>
                {t("quiz.progress", { current: index + 1, total: queue.length })}
            </div>

            <p className={styles.quizPrompt}>
                <QuestionPrompt type={item.type} />
            </p>

            <div className={styles.quizGlyph}>{item.symbol}</div>
            {item.type === "reading" && <p className={styles.quizHint}>{t("quiz.meaningHint", { meaning: getMeaning(item.symbol, language) })}</p>}

            {item.type !== "stroke" && (
                <div className={styles.quizOptions}>
                    {(item.type === "meaning" ? meaningOptions : readingData.options).map((option) => {
                        const correctOption = item.type === "meaning" ? getMeaning(item.symbol, language) : readingData.correct;
                        const isSelected = selected === option;
                        const showState = phase === "feedback";
                        const isCorrectOption = showState && option === correctOption;
                        const isWrongSelection = showState && isSelected && option !== correctOption;

                        return (
                            <button
                                key={option}
                                type="button"
                                className={[
                                    styles.quizOption,
                                    isCorrectOption ? styles.quizOptionCorrect : "",
                                    isWrongSelection ? styles.quizOptionWrong : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                onClick={() => handleOptionClick(option, correctOption)}
                                disabled={showState}
                            >
                                {option}
                            </button>
                        );
                    })}
                </div>
            )}

            {item.type === "stroke" && (
                <StrokeOrderDiagram
                    key={item.symbol}
                    symbol={item.symbol}
                    autoQuiz
                    onQuizComplete={(summary) => recordAnswer(summary.totalMistakes === 0)}
                />
            )}

            {phase === "feedback" && (
                <div className={wasCorrect ? styles.quizFeedbackCorrect : styles.quizFeedbackWrong}>
                    <FontAwesomeIcon icon={wasCorrect ? faCheck : faXmark} />
                    {wasCorrect ? t("quiz.correct") : t("quiz.notQuite")}
                    {!wasCorrect && item.type !== "stroke" && (
                        <span>
                            {" "}
                            {t("quiz.correctAnswer", {
                                answer: item.type === "meaning" ? getMeaning(item.symbol, language) : readingData.correct,
                            })}
                        </span>
                    )}
                </div>
            )}

            <div className={styles.quizActions}>
                <button type="button" className={styles.quizSecondaryButton} onClick={onExit}>
                    {t("quiz.endSession")}
                </button>
                {phase === "feedback" && (
                    <button type="button" className={styles.quizPrimaryButton} onClick={handleNext}>
                        {index + 1 >= queue.length ? t("quiz.seeResults") : t("quiz.next")}
                    </button>
                )}
            </div>
        </div>
    );
}

export function WanikaniQuizSummary({ score, missed, onRestart, onExit }) {
    const { t } = useTranslation("wanikaniPage");
    const lp = useLocalizedPath();
    const backToKanjiState = { from: "/wanikani", label: t("backLinkLabel") };
    return (
        <div className={styles.quizCard}>
            <h2 className={styles.cardTitle}>{t("quiz.sessionComplete")}</h2>
            <p className={styles.quizPrompt}>
                {t("quiz.score", { correct: score.correct, wrong: score.wrong })}
            </p>
            {missed.length > 0 && (
                <div>
                    <p className={styles.quizHint}>{t("quiz.kanjiToReview")}</p>
                    <div className={styles.quizMissedList}>
                        {missed.map((symbol) => (
                            <Link
                                key={symbol}
                                to={lp(`/kanji/${symbol}`)}
                                state={backToKanjiState}
                                className={styles.quizMissedTile}
                            >
                                {symbol}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
            <div className={styles.quizActions}>
                <button type="button" className={styles.quizSecondaryButton} onClick={onExit}>
                    {t("quiz.backToDashboard")}
                </button>
                <button type="button" className={styles.quizPrimaryButton} onClick={onRestart}>
                    {t("quiz.practiceAgain")}
                </button>
            </div>
        </div>
    );
}

export default WanikaniQuizComponent;
